import React, { useEffect, useState, type FC } from 'react';
import { useSearchParams } from 'react-router-dom';
import LoadingView from '../components/LoadingView';
import api from '../services/api';
import type CycleHistoryData from '../models/CycleHistoryDataModel';
import Table from '../components/Table';
import { getDayDiff, getTodayIso } from '../utils/date';

// eslint-disable-next-line complexity
const ChartStats: FC = () => {
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [cycleHistoryData, setCycleHistoryData] = useState<
        CycleHistoryData | undefined
    >();

    const [parameters] = useSearchParams();
    const userId = Number(parameters.get('id'));
    const cycleStart = parameters.get('start') ?? '';

    const calcBmi = (weight: number, height: number) => {
        // eslint-disable-next-line unicorn/prevent-abbreviations
        const retBmi = 703; // For weight in pounds and height in inches
        if (weight > 0 && height > 0) {
            return Math.round((weight / height ** 2) * retBmi * 10) / 10;
        }

        return undefined;
    };

    useEffect(() => {
        const pullStudentCycleHistory = async () => {
            const { cycleHistoryData, success, error } =
                await api.getStudentCycleHistory(userId, cycleStart);
            setCycleHistoryData(cycleHistoryData);
            if (!success) {
                setErrorMessage(error ?? 'unknown error');
            }

            setLoading(false);
        };

        void pullStudentCycleHistory();
    }, [userId, cycleStart]);

    const previousCycleVariationSection = (
        <div className="mb-8">
            <div className="text-primary text-2xl font-bold mb-2">
                Previous Cycle Variation
            </div>
            <div className="text-primary">
                Short: {cycleHistoryData?.short}{' '}
                {cycleHistoryData?.short === 1 ? 'day' : 'days'}
            </div>
            <div className="text-primary">
                Long: {cycleHistoryData?.long}{' '}
                {cycleHistoryData?.long === 1 ? 'day' : 'days'}
            </div>
            <div className="text-primary">
                Cycle variation based on: {cycleHistoryData?.cycleVarCount}{' '}
                recorded cycles
            </div>
            <div className="text-primary">
                Earliest first day of temperature rise: Day{' '}
                {cycleHistoryData?.earliestTemperatureRise}
            </div>
            <div className="text-primary">
                Based on last {cycleHistoryData?.numLastCycles}{' '}
                {cycleHistoryData?.numLastCycles === 1 ? 'cycle' : 'cycles'} (up
                to 12)
            </div>
        </div>
    );

    const endOfPhaseOneSection = (
        <div className="mb-8">
            <div className="text-primary text-2xl font-bold mb-2">
                End of Phase I
            </div>
            <div className="text-primary">
                Day 5/6 Rule:{' '}
                {cycleHistoryData?.day56Rule && cycleHistoryData.day56Rule > 0
                    ? `Day ${cycleHistoryData.day56Rule}`
                    : 'n/a'}
            </div>
            <div className="text-primary">
                Doering Rule:{' '}
                {cycleHistoryData?.doeringRule &&
                cycleHistoryData.doeringRule > 0 &&
                cycleHistoryData?.numLastCycles &&
                cycleHistoryData.numLastCycles > 6
                    ? `Day ${cycleHistoryData.doeringRule}`
                    : 'n/a'}
            </div>
        </div>
    );

    const bmiSection = (
        <div className="mb-8">
            <div className="text-primary text-2xl font-bold mb-2">
                Body Mass Index
            </div>
            <div className="text-primary">
                Weight:{' '}
                {cycleHistoryData?.weight
                    ? `${cycleHistoryData.weight} lbs`
                    : 'n/a'}
            </div>
            <div className="text-primary">
                Height:{' '}
                {cycleHistoryData?.height
                    ? `${cycleHistoryData.height} inches`
                    : 'n/a'}
            </div>
            <div className="text-primary">
                BMI:{' '}
                {cycleHistoryData?.weight && cycleHistoryData?.height
                    ? calcBmi(cycleHistoryData.weight, cycleHistoryData.height)
                    : 'n/a'}
            </div>
        </div>
    );

    const tableData = (cycleHistoryData?.cycles ?? []).map((cycle, index) => {
        const inProgress = index === 0 && cycle.start <= getTodayIso();
        const cycleLength = getDayDiff(cycle.start, cycle.end) + 1;
        const cycleLengthString = inProgress
            ? 'In Progress'
            : `${cycleLength} ${cycleLength === 1 ? 'day' : 'days'}`;

        let lutealLengthString;
        if (cycle.lutealLength > 0) {
            lutealLengthString = `${cycle.lutealLength} ${
                cycle.lutealLength === 1 ? 'day' : 'days'
            }`;
        } else if (inProgress) {
            lutealLengthString = 'n/a';
        } else {
            lutealLengthString = 'Unknown';
        }

        return {
            'Cycle Start': cycle.start,
            'Cycle Length': cycleLengthString,
            'Luteal Length': lutealLengthString,
        };
    });

    return (
        <>
            <div className="text-primary text-3xl pb-4">
                Chart Stats for {cycleStart} Cycle
            </div>
            {loading && <LoadingView />}
            {!loading && errorMessage && (
                <div className="text-primary text-xl">
                    Error: {errorMessage}
                </div>
            )}
            {!loading && !errorMessage && (
                <>
                    {previousCycleVariationSection}
                    {endOfPhaseOneSection}
                    {bmiSection}
                    <Table title="Recorded Cycles" data={tableData} />
                </>
            )}
        </>
    );
};

export default ChartStats;
